/**
 * Geolocation Permission Handling
 *
 * Handles requesting and checking location permissions using Capacitor Geolocation plugin.
 * Supports platform-specific permission behavior for iOS and Android.
 */

import { Geolocation } from '@capacitor/geolocation'
import type { PermissionStatus } from '@capacitor/geolocation'

/**
 * Request location permissions from the user
 * @returns Permission status after request
 */
export async function requestLocationPermissions(): Promise<PermissionStatus> {
    try {
        const status = await Geolocation.requestPermissions()
        return status
    } catch (error) {
        console.error('Failed to request location permissions:', error)
        throw new Error('Failed to request location permissions')
    }
}

/**
 * Check current location permission status
 * @returns Current permission status (granted, denied, prompt)
 */
export async function checkLocationPermissions(): Promise<PermissionStatus> {
    try {
        const status = await Geolocation.checkPermissions()
        return status
    } catch (error) {
        console.error('Failed to check location permissions:', error)
        throw new Error('Failed to check location permissions')
    }
}

/**
 * Check if location permissions are granted
 * @returns true if location permission is granted, false otherwise
 */
export async function hasLocationPermission(): Promise<boolean> {
    try {
        const status = await checkLocationPermissions()
        return status.location === 'granted'
    } catch (error) {
        console.error('Error checking location permission:', error)
        return false
    }
}
